import React from 'react';
import type { Language } from '../context/LanguageProvider';
import { useLanguage } from '../context/LanguageProvider';
import { useCookieConsent } from '../context/CookieContext';
import { getLandingTranslation } from '../landing/i18n';
import { trackCtaClick } from '../utils/ctaTracking';

const LanguageSwitcher = () => {
  const { language, setLanguage } = useLanguage();
  const { consent, isConsentSet } = useCookieConsent();
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);

  const flags: Record<Language, string> = {
    en: '🇬🇧',
    cs: '🇨🇿',
    de: '🇩🇪',
    fr: '🇫🇷',
    it: '🇮🇹',
    pl: '🇵🇱',
    es: '🇪🇸',
    ro: '🇷🇴',
    nl: '🇳🇱',
  };

  const t = getLandingTranslation(language);
  const registerUrl = 'https://beta.lucidbook.com/register/';
  const hasAnalyticsConsent = isConsentSet && consent.analytics;

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (!wrapperRef.current || (target && wrapperRef.current.contains(target))) {
        return;
      }

      setOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div className="relative z-50 flex items-center gap-3">
      <div ref={wrapperRef} className="relative inline-block text-left">
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="landing-cta-interactive inline-flex items-center gap-1.5 rounded-full border border-white/16 bg-white/8 px-2.5 py-1.5 font-pangram text-[11px] text-white backdrop-blur-md hover:border-white/24 hover:bg-white/14 sm:gap-2 sm:px-3 sm:py-2 sm:text-sm"
        >
          <span className="mr-1">{flags[language]}</span>
          <span>{language.toUpperCase()}</span>
          <span
            className={`inline-flex transform transition-transform duration-200 ${
              open ? 'rotate-180' : 'rotate-0'
            }`}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.361 7.87436L10.0035 13.2327L4.64514 7.87436"
                stroke="white"
                strokeWidth="1.5"
                strokeMiterlimit="10"
                strokeLinecap="square"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
        {open && (
          <div className="absolute right-0 z-50 mt-2 w-32 rounded-2xl border border-black/6 bg-white shadow-lg ring-1 ring-black/5 sm:w-36">
            <div className="py-2 text-black text-sm">
              {Object.entries(flags).map(([code, flag]) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => {
                    setLanguage(code as Language);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center px-3 py-2 hover:bg-gray-100 sm:px-4 ${
                    code === language ? 'font-semibold' : ''
                  }`}
                >
                  <span className="mr-2">{flag}</span>
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <a
        href={registerUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          trackCtaClick({
            placement: 'header_access',
            language,
            destination: registerUrl,
            hasAnalyticsConsent,
          })
        }
        className="landing-cta-interactive inline-flex items-center justify-center gap-3 rounded-full border border-white/16 bg-white/8 px-4 py-2 font-pangram text-sm leading-5 text-white backdrop-blur-md hover:bg-white/14 hover:border-white/24"
      >
        <span>{t.access}</span>
        <span className="landing-cta-dot inline-flex h-2.5 w-2.5 rounded-full bg-[#6195FF]" aria-hidden="true" />
      </a>
    </div>
  );
};

export default LanguageSwitcher;
